import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiDatabase, FiUsers, FiTrendingUp, FiCheckCircle, FiArrowRight, FiClock, FiAward, FiTarget } = FiIcons;

const DataGovernancePage = () => {
  const features = [
    {
      icon: FiDatabase,
      title: 'Data Catalog Management',
      description: 'Comprehensive data cataloging with automated metadata discovery and lineage tracking',
      benefits: [
        'Automated data source discovery',
        'Business glossary management',
        'Data lineage visualization',
        'Custom metadata schemas',
        'Impact analysis capabilities'
      ]
    },
    {
      icon: FiShield,
      title: 'Data Quality Framework',
      description: 'Establish data quality standards and monitoring for consistent, reliable data',
      benefits: [
        'Data quality rules engine',
        'Automated quality monitoring',
        'Data profiling and analysis',
        'Quality dashboards and reports',
        'Issue tracking and resolution'
      ]
    },
    {
      icon: FiUsers,
      title: 'Data Stewardship',
      description: 'Define roles, responsibilities, and workflows for effective data governance',
      benefits: [
        'Role-based access control',
        'Stewardship workflows',
        'Collaboration tools',
        'Change management processes',
        'Audit trail maintenance'
      ]
    },
    {
      icon: FiTrendingUp,
      title: 'Governance Analytics',
      description: 'Advanced analytics to measure and improve governance effectiveness',
      benefits: [
        'Governance maturity assessment',
        'KPI tracking and reporting',
        'Trend analysis and insights',
        'Performance benchmarking',
        'ROI measurement tools'
      ]
    }
  ];

  const process = [
    { step: '01', title: 'Assessment & Planning', description: 'Evaluate current governance maturity and develop comprehensive roadmap', duration: '1-2 weeks' },
    { step: '02', title: 'Framework Design', description: 'Create customized governance policies, standards, and procedures', duration: '2-3 weeks' },
    { step: '03', title: 'Implementation', description: 'Deploy governance tools and establish stewardship programs', duration: '4-6 weeks' },
    { step: '04', title: 'Monitoring & Optimization', description: 'Continuous monitoring and improvement of governance practices', duration: 'Ongoing' }
  ];

  const benefits = [
    { metric: '85%', description: 'Improvement in data quality' },
    { metric: '60%', description: 'Reduction in data-related issues' },
    { metric: '90%', description: 'Faster data discovery' },
    { metric: '75%', description: 'Better regulatory compliance' }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Enterprise Data Governance Excellence
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                Transform your organization's data into a trusted, strategic asset with our comprehensive data governance framework powered by Microsoft Purview.
              </p>
              <Link
                to="/purview-audit"
                className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105"
              >
                Get Purview Governance Assessment
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Data Governance Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Effective data governance delivers measurable business value across your organization
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-blue-600 mb-2">{benefit.metric}</div>
                <div className="text-gray-600">{benefit.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Governance Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our data governance solutions address every aspect of managing enterprise data effectively
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg shadow-lg"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <SafeIcon icon={feature.icon} className="h-8 w-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Governance Implementation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A structured approach to establishing effective data governance in your organization
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <div className="text-sm text-blue-600 font-semibold">{step.duration}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Data Governance?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Let our experts help you build a robust data governance framework that drives business value
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/purview-audit"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all transform hover:scale-105"
            >
              Get Purview Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataGovernancePage;